// TICKET-9 🟡 Fichier sans aucun test (290 lignes, génération données démo)
// À créer : demoWorkspace.test.ts — tester createDemoWorkspaceBundle, mergeDemoWorkspace

import {
  CalcMissionSession,
  CraDayState,
  CraMonth,
  DealRoom,
  Evaluation,
  Moscow,
  ProjectContext,
  Raci,
  Sipoc,
  TreasuryProfile,
  UserProfile,
  Workspace,
} from '@shared/types';
import { createCalcMissionSession } from './calcMission';

export const DEMO_PROJECT_ID = 'demo-studio-nacre';

function dayKey(year: number, month: number, day: number) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function createDemoCraStates(year: number, month: number): Record<string, CraDayState> {
  const states: Record<string, CraDayState> = {};
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  let worked = 0;
  let demi = 0;

  for (let day = 1; day <= daysInMonth; day += 1) {
    const weekday = new Date(year, month, day).getDay();
    if (weekday === 0 || weekday === 6) continue;

    const key = dayKey(year, month, day);
    if (worked < 9) {
      states[key] = 'travaille';
      worked += 1;
    } else if (demi < 2) {
      states[key] = 'demi';
      demi += 1;
    } else if (day % 7 === 0) {
      states[key] = 'conge';
    }
  }

  return states;
}

export function createDemoWorkspaceBundle(referenceDate = new Date()): Pick<
  Workspace,
  'profile' | 'projects' | 'evaluations' | 'sipocs' | 'moscows' | 'racis' | 'dealRooms' | 'cras' | 'treasuries' | 'calcMissionSessions' | 'currentCalcMissionSessionId' | 'variables' | 'notes' | 'timeline'
> {
  const now = referenceDate.getTime();
  const profile: UserProfile = {
    id: 'demo-profile',
    firstName: 'Lea',
    lastName: 'Martin',
    email: 'lea@studionacre.fr',
    company: 'Studio Nacre',
    role: 'Consultante organisation et experience client',
    mode: 'expert',
    organizationType: 'freelance',
    status: 'ei',
    netTarget: 3800,
    tjm: 620,
    daysPerMonth: 12,
    headline: 'Une vue simple pour cadrer, vendre et suivre ses projets.',
    createdAt: now,
    updatedAt: now,
  };
  const project: ProjectContext = {
    id: DEMO_PROJECT_ID,
    name: 'Studio Nacre - Refonte experience client',
    client: 'Studio Nacre',
    owner: 'Lea Martin',
    description: 'Refondre le parcours client, clarifier les offres et produire un support simple a partager.',
    scope: 'Audit, organisation des priorites, support client et suivi de l activite.',
    budget: 12000,
    tjm: 620,
    createdAt: now,
    updatedAt: now,
  };

  const evaluation: Evaluation = {
    id: 'demo-eval',
    projectId: project.id,
    scores: {
      strategicAlignment: 5,
      technicalComplexity: 4,
      teamReadiness: 4,
      businessValue: 5,
      riskLevel: 4,
    },
    verdict: 'GO',
    totalScore: 88,
    comments: 'Projet clair, visible, avec un perimetre raisonnable pour un solo freelance.',
    recommendations: ['Commencer par la vue du projet', 'Poser des priorites simples', 'Suivre les jours et l argent au fil de l eau'],
    calculatedAt: now,
    meta: {
      version: 1,
      calculatedBy: 'demo-seed',
    },
  };

  const sipoc: Sipoc = {
    id: 'demo-sipoc',
    projectId: project.id,
    name: project.name,
    owner: project.owner,
    suppliers: { items: [{ id: 'demo-sup-1', title: 'Client', order: 0 }, { id: 'demo-sup-2', title: 'Freelance', order: 1 }] },
    inputs: { items: [{ id: 'demo-in-1', title: 'Brief initial', order: 0 }, { id: 'demo-in-2', title: 'Offres existantes', order: 1 }] },
    process: { items: [{ id: 'demo-proc-1', title: 'Comprendre le besoin', order: 0 }, { id: 'demo-proc-2', title: 'Organiser les priorites', order: 1 }, { id: 'demo-proc-3', title: 'Produire le support client', order: 2 }] },
    outputs: { items: [{ id: 'demo-out-1', title: 'Plan d action simple', order: 0 }, { id: 'demo-out-2', title: 'Synthèse client partageable', order: 1 }] },
    customers: { items: [{ id: 'demo-cus-1', title: 'Studio Nacre', order: 0 }] },
    projectScope: project.scope,
    scope: 'BUSINESS',
    createdAt: now,
    updatedAt: now,
  };

  const moscow: Moscow = {
    id: 'demo-moscow',
    projectId: project.id,
    name: 'Priorites Studio Nacre',
    budgetDays: 12,
    teamSize: 2,
    categories: {
      must: { items: [{ id: 'demo-must-1', title: 'Clarifier les offres', effort: 1, order: 0 }, { id: 'demo-must-2', title: 'Créer une synthèse client', effort: 1, order: 1 }] },
      should: { items: [{ id: 'demo-should-1', title: 'Planifier les prochaines actions', effort: 1, order: 0 }] },
      could: { items: [{ id: 'demo-could-1', title: 'Préparer une page de presentation', effort: 1, order: 0 }] },
      wont: { items: [{ id: 'demo-wont-1', title: 'Refonte complete du site', effort: 1, order: 0 }] },
    },
    createdAt: now,
    updatedAt: now,
  };

  const raci: Raci = {
    id: 'demo-raci',
    projectId: project.id,
    name: 'Roles Studio Nacre',
    sponsor: project.owner,
    roles: [{ id: 'demo-role-1', name: 'Vous' }, { id: 'demo-role-2', name: 'Client' }, { id: 'demo-role-3', name: 'Partenaire' }],
    tasks: [{ id: 'demo-task-1', title: 'Clarifier les offres', mustHave: true, order: 0 }, { id: 'demo-task-2', title: 'Rédiger la synthèse client', mustHave: true, order: 1 }, { id: 'demo-task-3', title: 'Valider le prochain rendez-vous', shouldHave: true, order: 2 }],
    matrix: [
      { taskId: 'demo-task-1', roleId: 'demo-role-1', assignment: 'R' },
      { taskId: 'demo-task-1', roleId: 'demo-role-2', assignment: 'A' },
      { taskId: 'demo-task-2', roleId: 'demo-role-1', assignment: 'R' },
      { taskId: 'demo-task-2', roleId: 'demo-role-2', assignment: 'A' },
      { taskId: 'demo-task-3', roleId: 'demo-role-1', assignment: 'R' },
      { taskId: 'demo-task-3', roleId: 'demo-role-3', assignment: 'C' },
    ],
    createdAt: now,
    updatedAt: now,
  };

  const dealRoom: DealRoom = {
    id: 'demo-deal',
    projectId: project.id,
    name: project.name,
    client: project.client,
    owner: project.owner,
    status: 'cadre',
    objective: 'Rendre l offre plus lisible et donner une vue simple des prochaines etapes.',
    context: 'Le client a plusieurs offres, mais elles sont peu claires pour un prospect exterieur.',
    scope: project.scope,
    deliverables: ['Plan d action priorise', 'Synthèse client partageable', 'Liste des prochaines actions'],
    tasks: [{ id: 'demo-deal-task-1', title: 'Valider les priorites', status: 'doing' }, { id: 'demo-deal-task-2', title: 'Partager la synthèse client', status: 'todo' }, { id: 'demo-deal-task-3', title: 'Préparer le prochain point', status: 'todo' }],
    milestones: [
      { id: 'demo-ms-1', title: 'Cadrage validé', date: '2026-05-15', done: true },
      { id: 'demo-ms-2', title: 'Wireframes approuvés', date: '2026-06-01', done: false },
      { id: 'demo-ms-3', title: 'Mise en production', date: '2026-07-15', done: false },
    ],
    risks: ['Décisions tardives du client', 'Besoin de valider les contenus avant diffusion'],
    decisions: ['Commencer par les offres les plus vendues', 'Partager un support simple avant toute refonte lourde'],
    nextSteps: 'Valider les wireframes lors du prochain point client. Preparer la recette utilisateur.',
    createdAt: now,
    updatedAt: now,
  };

  const craYear = referenceDate.getFullYear();
  const craMonth = referenceDate.getMonth();
  const craStates = createDemoCraStates(craYear, craMonth);
  const workedDays = Object.values(craStates).reduce((sum, value) => sum + (value === 'travaille' ? 1 : value === 'demi' ? 0.5 : 0), 0);
  const leaveDays = Object.values(craStates).filter((value) => value === 'conge').length;
  const cra: CraMonth = {
    id: 'demo-cra',
    projectId: project.id,
    year: craYear,
    month: craMonth,
    consultant: project.owner,
    client: project.client,
    mission: project.name,
    tjm: project.tjm || 620,
    dayStates: craStates,
    workedDays,
    leaveDays,
    revenue: workedDays * (project.tjm || 620),
    createdAt: now,
    updatedAt: now,
  };

  const treasury: TreasuryProfile = {
    id: 'demo-treasury',
    projectId: project.id,
    status: 'ei',
    openingBalance: 7800,
    caYtd: 16400,
    horizonMonths: 6,
    daysPerMonth: 12,
    tjm: project.tjm || 620,
    charges: [
      { id: 'demo-charge-1', label: 'Outils et logiciels', amount: 95, kind: 'fixe' },
      { id: 'demo-charge-2', label: 'Assurance et banque', amount: 85, kind: 'fixe' },
      { id: 'demo-charge-3', label: 'Deplacements et impressions', amount: 180, kind: 'variable' },
    ],
    createdAt: now,
    updatedAt: now,
  };

  const calcSession: CalcMissionSession = {
    ...createCalcMissionSession({
      name: 'Studio Nacre - cadrage',
      projectId: project.id,
      profile,
      templateKey: 'forfait-cadre',
    }),
    simple: {
      name: project.name,
      client: project.client,
      tjm: 620,
      days: 12,
      fixedCosts: 240,
      variableCosts: 180,
      extraCosts: 120,
      netTarget: 3800,
      successRate: 100,
      contingencyPct: 12,
      status: 'ei',
    },
    scratchpad: [
      '# Studio Nacre',
      'tjm = 620',
      'jours_cadrage = 4',
      'jours_support = 8',
      'jours_total = jours_cadrage + jours_support',
      'budget = tjm * jours_total',
      'frais = 240 + 180',
      'reste = budget - frais',
    ].join('\n'),
  };

  return {
    profile,
    projects: [project],
    evaluations: [evaluation],
    sipocs: [sipoc],
    moscows: [moscow],
    racis: [raci],
    dealRooms: [dealRoom],
    cras: [cra],
    treasuries: [treasury],
    calcMissionSessions: [calcSession],
    currentCalcMissionSessionId: calcSession.id,
    variables: { tjm: 620, jours: 12, budget: 7440, marge: 0.28 },
    notes: [],
    timeline: [],
  };
}

export function mergeDemoWorkspace(workspace: Workspace): Workspace {
  if (workspace.meta?.demoDismissed) return workspace;
  if (workspace.projects.some((project) => project.id === DEMO_PROJECT_ID)) return workspace;
  const demo = createDemoWorkspaceBundle();

  return {
    ...workspace,
    currentProjectId: workspace.currentProjectId || DEMO_PROJECT_ID,
    currentCalcMissionSessionId: workspace.currentCalcMissionSessionId || demo.currentCalcMissionSessionId,
    profile: workspace.profile.firstName || workspace.profile.company ? workspace.profile : demo.profile,
    projects: [...workspace.projects, ...demo.projects],
    evaluations: [...workspace.evaluations, ...demo.evaluations],
    sipocs: [...workspace.sipocs, ...demo.sipocs],
    moscows: [...workspace.moscows, ...demo.moscows],
    racis: [...workspace.racis, ...demo.racis],
    dealRooms: [...workspace.dealRooms, ...demo.dealRooms],
    cras: [...workspace.cras, ...demo.cras],
    treasuries: [...workspace.treasuries, ...demo.treasuries],
    calcMissionSessions: [...workspace.calcMissionSessions, ...demo.calcMissionSessions],
    variables: { ...workspace.variables, ...demo.variables },
  };
}
